import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { DollarSign, RefreshCw, Info } from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Cell,
  CartesianGrid,
} from "recharts";
import {
  CHART_COLORS,
  fmtMonthLabel,
  trimLeadingEmpty,
  filterByRange,
  loadRange,
  saveRange,
  loadCustomRange,
  saveCustomRange,
  validateCustomRange,
  ymKey,
} from "@/lib/chartUtils";
import ChartRangePicker from "@/components/ChartRangePicker";

function fmtMoney(v, currency) {
  if (v == null) return "—";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
      maximumFractionDigits: 0,
    }).format(v);
  } catch {
    return `${currency || ""} ${Math.round(v).toLocaleString()}`;
  }
}

function pct(v) {
  return v == null ? "—" : `${Math.round(v * 100)}%`;
}

export default function BookingsPage() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [range, setRange] = useState(loadRange("rc:bookings:range", "last_12m"));
  const [customRange, setCustomRange] = useState(() => loadCustomRange("rc:bookings:custom"));
  const nav = useNavigate();

  const load = async () => {
    setErr("");
    try {
      const { data } = await api.get("/metrics/latest");
      setData(data);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const recompute = async () => {
    setBusy(true);
    try {
      await api.post("/metrics/recompute");
      await load();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!data) {
    return (
      <div className="max-w-3xl mx-auto w-full p-6 lg:p-10 text-sm text-slate-500" data-testid="bookings-loading">
        Loading bookings…
      </div>
    );
  }

  if (data.empty) {
    return (
      <div className="max-w-3xl mx-auto w-full p-6 lg:p-10" data-testid="bookings-empty-state">
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center flex flex-col items-center">
          <DollarSign className="h-10 w-10 text-slate-400" />
          <h2 className="mt-4 text-xl font-semibold text-slate-900">No bookings data yet</h2>
          <p className="mt-2 text-sm text-slate-500 max-w-sm">
            Bookings analysis appears once your first snapshot + stage mapping are in place.
          </p>
          <button
            onClick={() => nav("/dashboard/connect")}
            data-testid="bookings-setup-cta"
            className="mt-6 inline-flex items-center gap-2 rounded-lg bg-slate-900 text-white px-4 py-2 text-sm font-semibold hover:bg-slate-800 transition-colors"
          >
            Set up now
          </button>
        </div>
      </div>
    );
  }

  const m = data.metrics;
  const be = m.bookings_extended || {};
  const cur = m.currency || "USD";
  const monthly = be.monthly_series || [];
  const acv = be.acv_trend || [];
  const winTrend = be.win_rate_trend || [];
  const accHist = be.forecast_accuracy_history || [];
  const currentMonthKey = ymKey(new Date());

  // Apply user's range + trim leading empty months for cleaner charts
  const validCustom = range === "custom" && !validateCustomRange(customRange?.from, customRange?.to) ? customRange : null;
  const monthlyFiltered = trimLeadingEmpty(filterByRange(monthly, range, validCustom), "won_amount", 3);
  const acvFiltered = trimLeadingEmpty(filterByRange(acv, range, validCustom), "avg", 3);
  const winFiltered = trimLeadingEmpty(filterByRange(winTrend, range, validCustom), "rate", 3);
  const nonZeroMonths = monthlyFiltered.filter((mo) => mo.won_amount > 0 || mo.deal_count > 0);
  // find latest & prior full month (from unfiltered series, always the same)
  const latest = monthly[monthly.length - 1];
  const prev = monthly[monthly.length - 2];

  return (
    <div className="max-w-6xl mx-auto w-full p-6 lg:p-10 space-y-8" data-testid="bookings-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500">Bookings</div>
          <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold tracking-tighter text-slate-900">
            What you actually closed
          </h1>
          <p className="mt-3 text-slate-500 max-w-xl">
            Won amounts by month, forecast accuracy from your history, deal-size and win-rate
            trends. Snapshot {m.metric_date}.
          </p>
        </div>
        <button
          onClick={recompute}
          disabled={busy}
          data-testid="bookings-recompute-btn"
          className="inline-flex items-center gap-2 rounded-lg bg-white ring-1 ring-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-60"
        >
          <RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} />
          Recompute
        </button>
      </header>

      {/* Top KPIs from base bookings */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="bookings-kpis">
        <KPI label="MTD" value={fmtMoney(m.bookings.month_to_date, cur)} sub={`prev month ${fmtMoney(m.bookings.prev_month, cur)}`} />
        <KPI label="QTD" value={fmtMoney(m.bookings.quarter_to_date, cur)} sub={`prev quarter ${fmtMoney(m.bookings.prev_quarter, cur)}`} />
        <KPI label="Latest month" value={fmtMoney(latest?.won_amount, cur)} sub={latest?.month || ""} />
        <KPI label="Latest month deals" value={latest?.deal_count ?? 0} sub={latest?.month || ""} />
      </section>

      {/* Monthly bar chart */}
      <section data-testid="bookings-monthly-section">
        <div className="flex items-baseline justify-between flex-wrap gap-2 mb-4">
          <h2 className="text-xl font-semibold text-slate-900">Bookings</h2>
          <ChartRangePicker
            value={range}
            onChange={(id) => { setRange(id); saveRange("rc:bookings:range", id); }}
            customRange={customRange}
            onCustomChange={(r) => { setCustomRange(r); saveCustomRange("rc:bookings:custom", r); }}
            testid="bookings-range-picker"
          />
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 h-72" style={{ minHeight: 280 }}>
          {nonZeroMonths.length === 0 ? (
            <div className="h-full flex items-center justify-center text-sm text-slate-500" data-testid="bookings-monthly-empty">
              No won deals in the selected range.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%" minHeight={240}>
              <BarChart data={monthlyFiltered} margin={{ top: 8, right: 12, bottom: 4, left: 4 }}>
                <CartesianGrid vertical={false} stroke={CHART_COLORS.grid} strokeDasharray="3 3" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: CHART_COLORS.axis }} tickFormatter={fmtMonthLabel} tickLine={false} axisLine={{ stroke: CHART_COLORS.grid }} />
                <YAxis tick={{ fontSize: 11, fill: CHART_COLORS.axis }} tickFormatter={(v) => (v ? `${Math.round(v / 1000)}k` : "")} width={44} tickLine={false} axisLine={false} />
                <Tooltip
                  formatter={(v) => fmtMoney(v, cur)}
                  labelFormatter={fmtMonthLabel}
                  contentStyle={{ fontSize: 12, border: `1px solid ${CHART_COLORS.grid}`, borderRadius: 8 }}
                />
                <Bar dataKey="won_amount" radius={[6, 6, 0, 0]}>
                  {monthlyFiltered.map((mo, i) => (
                    <Cell key={i} fill={mo.month === currentMonthKey ? CHART_COLORS.primary : CHART_COLORS.primaryTint} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </section>

      {/* Forecast accuracy history */}
      <section data-testid="bookings-forecast-accuracy-section">
        <h2 className="text-xl font-semibold text-slate-900 mb-4">Forecast accuracy — history</h2>
        {accHist.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500" data-testid="bookings-accuracy-empty">
            Forecast accuracy accumulates as full quarters end. First value will appear once your
            first snapshot is at least one quarter old.
          </div>
        ) : (
          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
            <div className="grid grid-cols-12 px-6 py-3 border-b border-slate-100 bg-slate-50 text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
              <div className="col-span-3">Period</div>
              <div className="col-span-3 text-right">Predicted</div>
              <div className="col-span-3 text-right">Actual won</div>
              <div className="col-span-3 text-right">Accuracy</div>
            </div>
            {accHist.map((a) => (
              <div key={a.period} className="grid grid-cols-12 px-6 py-4 border-b border-slate-100 last:border-b-0 items-center">
                <div className="col-span-3 text-sm font-semibold text-slate-900">{a.period}</div>
                <div className="col-span-3 text-right text-sm text-slate-700 number-tabular">{fmtMoney(a.forecast_predicted, cur)}</div>
                <div className="col-span-3 text-right text-sm text-slate-700 number-tabular">{fmtMoney(a.actual_won, cur)}</div>
                <div className={`col-span-3 text-right text-sm font-semibold number-tabular ${
                  a.accuracy == null ? "text-slate-400" : a.accuracy >= 0.9 && a.accuracy <= 1.1 ? "text-emerald-600" : a.accuracy >= 0.75 && a.accuracy <= 1.25 ? "text-amber-600" : "text-red-600"
                }`}>
                  {pct(a.accuracy)}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Avg deal size trend + win rate trend */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="bookings-trends-section">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 h-64" style={{ minHeight: 240 }}>
          <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500 mb-2">Avg deal size (won)</div>
          <ResponsiveContainer width="100%" height="90%" minHeight={200}>
            <LineChart data={acvFiltered} margin={{ top: 8, right: 12, bottom: 4, left: 4 }}>
              <CartesianGrid vertical={false} stroke={CHART_COLORS.grid} strokeDasharray="3 3" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: CHART_COLORS.axis }} tickFormatter={fmtMonthLabel} tickLine={false} axisLine={{ stroke: CHART_COLORS.grid }} />
              <YAxis tick={{ fontSize: 11, fill: CHART_COLORS.axis }} tickFormatter={(v) => (v ? `${Math.round(v / 1000)}k` : "")} width={44} tickLine={false} axisLine={false} />
              <Tooltip formatter={(v) => fmtMoney(v, cur)} labelFormatter={fmtMonthLabel} contentStyle={{ fontSize: 12, border: `1px solid ${CHART_COLORS.grid}`, borderRadius: 8 }} />
              <Line type="monotone" dataKey="avg" stroke={CHART_COLORS.primary} strokeWidth={2} dot={{ r: 3, fill: CHART_COLORS.primary }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 h-64" style={{ minHeight: 240 }}>
          <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500 mb-2">Win rate trend</div>
          <ResponsiveContainer width="100%" height="90%" minHeight={200}>
            <LineChart data={winFiltered} margin={{ top: 8, right: 12, bottom: 4, left: 4 }}>
              <CartesianGrid vertical={false} stroke={CHART_COLORS.grid} strokeDasharray="3 3" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: CHART_COLORS.axis }} tickFormatter={fmtMonthLabel} tickLine={false} axisLine={{ stroke: CHART_COLORS.grid }} />
              <YAxis tick={{ fontSize: 11, fill: CHART_COLORS.axis }} tickFormatter={(v) => `${Math.round(v * 100)}%`} domain={[0, 1]} width={44} tickLine={false} axisLine={false} />
              <Tooltip formatter={(v) => pct(v)} labelFormatter={fmtMonthLabel} contentStyle={{ fontSize: 12, border: `1px solid ${CHART_COLORS.grid}`, borderRadius: 8 }} />
              <Line type="monotone" dataKey="rate" stroke={CHART_COLORS.positive} strokeWidth={2} dot={{ r: 3, fill: CHART_COLORS.positive }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </section>

      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-xs text-slate-600 flex items-start gap-2">
        <Info className="h-4 w-4 text-slate-400 flex-shrink-0 mt-0.5" />
        <div>
          Forecast accuracy compares the <span className="font-semibold">weighted forecast recorded at the start of a quarter</span> to
          the actual won amount for that quarter. History accumulates automatically.
        </div>
      </div>

      {err && <div className="text-sm text-red-600" data-testid="bookings-error">{err}</div>}
    </div>
  );
}

function KPI({ label, value, sub }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500">{label}</div>
      <div className="mt-1 text-xl font-extrabold text-slate-900 number-tabular">{value}</div>
      <div className="text-xs text-slate-500 mt-1">{sub}</div>
    </div>
  );
}
